import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip } from '../lib/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShareNetwork, Lock } from '@phosphor-icons/react';
import { effectiveHolePars, effectiveStrokeIndexes, shotsOnHole, isRoundFrozen, revealHoleForRound, holeInPlayingRange, isMultiStartRound, formatPlayingHandicap, getEffectivePH, displayWHS } from '../golf';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';
import { roundFormat, formatValueLabel } from '../lib/formatHelpers';
import { groupDisplayName } from '../lib/groupOrder';
import { computeTeamPH, driveCountsForTeam, computeScrambleLeaderboard } from '../lib/scramble';
import { getTripAccess } from '../lib/tripAccess';
import { toPng } from 'html-to-image';

function formatDateRange(start, end) {
  const fmt = (s) => { if (!s) return null; const d = new Date(s + 'T12:00:00'); return isNaN(d) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); };
  const a = fmt(start), b = fmt(end);
  if (!a && !b) return '';
  if (!a) return b;
  if (!b || a === b) return a;
  const da = new Date(start + 'T12:00:00'), db = new Date(end + 'T12:00:00');
  if (da.getFullYear() === db.getFullYear()) {
    const s = da.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${s} to ${b}`;
  }
  return `${a} to ${b}`;
}

function scoreColorClass(strokes, par) {
  if (!strokes || strokes <= 0) return 'score-unscored';
  const diff = strokes - par;
  if (diff <= -2) return 'score-eagle';
  if (diff === -1) return 'score-birdie';
  if (diff === 0) return 'score-par';
  if (diff === 1) return 'score-bogey';
  if (diff === 2) return 'score-double-bogey';
  return 'score-triple-plus';
}

export default function ScrambleScorecardView() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const cacheKey = `foretour:trip:${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [round, setRound] = useState(() => trip?.rounds?.find(r => r.id === roundId) || null);
  const [loading, setLoading] = useState(!trip || !round);
  const [exporting, setExporting] = useState(false);
  const [displayMode, setDisplayMode] = useState('strokes');
  const [filterMode, setFilterMode] = useState('all');
  const [filterTeamIdx, setFilterTeamIdx] = useState(null);
  const [filterPlayerId, setFilterPlayerId] = useState(null);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code);
      setTrip(res.data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch {}
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setLoading(false); }
  }, [code, roundId, cacheKey]);
  useEffect(() => { load(); }, [load]);

  const players = trip?.players || [];
  const teams = round?.teams || [];
  const holePars = round ? effectiveHolePars(round) : Array(18).fill(4);
  const strokeIndexes = round ? effectiveStrokeIndexes(round) : Array.from({length: 18}, (_, i) => i + 1);
  const outPar = holePars.slice(0, 9).reduce((s, v) => s + v, 0);
  const inPar = holePars.slice(9).reduce((s, v) => s + v, 0);
  const freezeAt = round ? revealHoleForRound(round) : 18;
  const isStableford = round ? roundFormat(round) !== 'nsp' : false;
  const minDrives = round?.scramble_config?.min_drives ?? 0;

  // Scramble freeze: isRoundFrozen checks individual hole_strokes (empty for scramble).
  // Override: check team_hole_strokes for any team that scored at/past the reveal hole.
  const frozen = (() => {
    if (!round || round.revealed || round.freeze_enabled === false) return false;
    const reveal = revealHoleForRound(round);
    // "From the start" (reveal_hole=0): frozen immediately
    if (reveal === 0) return true;
    for (let t = 0; t < teams.length; t++) {
      const ts = round.team_hole_strokes?.[String(t)] || [];
      if ((ts[reveal - 1] ?? 0) > 0) return true;
    }
    return false;
  })();

  // ── Freeze visibility: server-side null scrub handles data confidentiality.
  // Frontend shows every team; lock icons come from frozenHoles set. ──
  const access = getTripAccess(code);
  const isOrganiser = access?.role === 'organiser' || !!access?.legacy;
  const showFreezeHidden = false;
  const freezeTeamIdx = null;

  // Sort teams by leaderboard position
  const standings = round && trip ? computeScrambleLeaderboard(round, trip) : [];

  const handleExport = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const card = cardRef.current;
      const tableWrap = card.querySelector('[data-scroll-wrap]');
      card.style.minWidth = '860px';
      card.style.maxWidth = 'none';
      if (tableWrap) tableWrap.style.overflow = 'visible';
      const dataUrl = await toPng(card, {
        backgroundColor: '#FFFFFF',
        pixelRatio: 2,
        cacheBust: true,
        style: { padding: '16px' },
      });
      card.style.minWidth = '';
      card.style.maxWidth = '';
      if (tableWrap) tableWrap.style.overflow = '';
      if (navigator.share && navigator.canShare) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], `${trip?.name}-${round?.label}-Teams.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title: `${trip?.name} — ${round?.label}`, files: [file] });
            setExporting(false);
            return;
          }
        } catch {}
      }
      const link = document.createElement('a');
      link.download = `${trip?.name || 'scorecard'}-${round?.label || 'round'}-Teams.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) { console.error('Export failed', e); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;
  if (!round) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  const formatLabel = roundFormat(round) === 'nsp' ? 'Net Stroke Play' : 'Stableford';

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="scramble-scorecard-screen">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">Scorecard</h1><p className="text-xs text-[#B8C7C0]">{round.label} · {round.course?.name}</p></div>
        <Button size="sm" onClick={handleExport} disabled={exporting} className="bg-[#1B4332] text-white h-8 rounded-lg gap-1" data-testid="export-btn">
          <ShareNetwork className="w-4 h-4" weight="bold" /> {exporting ? '...' : 'Share'}
        </Button>
      </div>

      {/* Strokes / Points toggle — Stableford only */}
      {isStableford && (
        <div className="flex justify-center px-4 pt-3 pb-1" data-testid="scorecard-display-toggle">
          <div className="inline-flex rounded-lg bg-[#E1E6E4] p-0.5">
            <button onClick={() => setDisplayMode('strokes')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${displayMode === 'strokes' ? 'bg-white text-[#1D242B] shadow-sm' : 'text-[#455561]'}`}
              data-testid="toggle-strokes">Strokes</button>
            <button onClick={() => setDisplayMode('points')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${displayMode === 'points' ? 'bg-white text-[#1D242B] shadow-sm' : 'text-[#455561]'}`}
              data-testid="toggle-points">Stableford</button>
          </div>
        </div>
      )}

      <div className="px-2 py-4">
        <div ref={cardRef} className="max-w-full mx-auto bg-white rounded-xl border border-[#E1E6E4] overflow-hidden">
          {/* Header with branding */}
          <div className="bg-[#1B4332] text-white px-4 py-3 relative">
            <h2 className="text-sm font-bold leading-snug" style={{overflowWrap: 'break-word'}}>{trip?.name}</h2>
            {(trip?.start_date || trip?.end_date) && (
              <p className="text-[10px] text-[#B8C7C0] mt-0.5">{formatDateRange(trip.start_date, trip.end_date)}</p>
            )}
            <p className="text-xs text-white/80 mt-1">{round.label} · {round.course?.name}</p>
            <p className="text-[10px] text-[#B8C7C0] mt-0.5">
              <span className="whitespace-nowrap">Par&nbsp;{round.course?.par}</span>
              {round.course?.tee_name && <> · {round.course.tee_name}</>}
              {round.course?.course_rating && <> · CR {round.course.course_rating}</>}
              {round.course?.slope_rating && <> · Slope {round.course.slope_rating}</>}
            </p>
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#C9A24A] text-[#1B4332]">
              Texas Scramble · {formatLabel}
            </span>
            <p className="text-[10px] font-bold text-[#C9A24A] absolute bottom-3 right-4">Fore Tour</p>
          </div>

          {/* Freeze-restricted: hide scores for non-organiser without own-team claim */}
          {showFreezeHidden ? (
            <div className="px-4 py-12 text-center" data-testid="scorecard-frozen-hidden">
              <Lock className="w-8 h-8 text-[#B8C7C0] mx-auto mb-3" weight="bold" />
              <p className="text-[#455561] text-sm">Scores are hidden until the organiser reveals the results.</p>
            </div>
          ) : (
          <>
          {/* Filter control — hidden when freeze restricts to own team */}
          {freezeTeamIdx == null && (
          <div className="px-2 py-1.5 flex items-center gap-1.5 flex-wrap bg-[#F4F6F5] border-b border-[#E1E6E4]" data-testid="scorecard-filter">
            {['all', 'group', 'player'].map(m => (
              <button key={m} onClick={() => { setFilterMode(m); if (m === 'all') { setFilterTeamIdx(null); setFilterPlayerId(null); } }}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${filterMode === m ? 'bg-[#1B4332] text-white' : 'bg-white text-[#455561] border border-[#E1E6E4]'}`}
                data-testid={`filter-${m}`}>
                {m === 'all' ? 'All teams' : m === 'group' ? 'Team' : 'Player'}
              </button>
            ))}
            {filterMode === 'group' && teams.length > 0 && (
              <select value={filterTeamIdx ?? ''} onChange={e => setFilterTeamIdx(e.target.value === '' ? null : Number(e.target.value))}
                className="px-2 py-1 rounded-md text-[11px] border border-[#E1E6E4] bg-white text-[#1D242B] min-w-0"
                data-testid="filter-group-select">
                <option value="">Choose team</option>
                {teams.map((pids, i) => <option key={i} value={i}>{groupDisplayName(round, i)} ({pids.length})</option>)}
              </select>
            )}
            {filterMode === 'player' && (
              <select value={filterPlayerId ?? ''} onChange={e => setFilterPlayerId(e.target.value || null)}
                className="px-2 py-1 rounded-md text-[11px] border border-[#E1E6E4] bg-white text-[#1D242B] min-w-0"
                data-testid="filter-player-select">
                <option value="">Choose player</option>
                {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
          )}

          {/* Scorecard table */}
          <div className="p-2 overflow-x-auto" data-scroll-wrap>
            <table className="w-full text-xs border-collapse" data-testid="scramble-scorecard-table">
              <thead>
                <tr className="bg-[#1B4332] text-white">
                  <th className="px-2 py-2 text-left font-semibold w-36 sticky left-0 bg-[#1B4332] z-10">Team</th>
                  {Array.from({length: 9}, (_, i) => <th key={i} className="px-1 py-2 text-center font-semibold w-8">{i + 1}</th>)}
                  <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">OUT</th>
                  {Array.from({length: 9}, (_, i) => <th key={i + 9} className="px-1 py-2 text-center font-semibold w-8">{i + 10}</th>)}
                  <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">IN</th>
                  <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">{frozen ? `Thru ${freezeAt}` : 'Total'}</th>
                  <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">{isStableford && displayMode === 'strokes' ? 'Net' : formatValueLabel(roundFormat(round))}</th>
                </tr>
                <tr className="bg-[#F4F6F5]">
                  <td className="px-2 py-1.5 font-semibold text-[#455561] sticky left-0 bg-[#F4F6F5] z-10">Par</td>
                  {holePars.slice(0, 9).map((p, i) => <td key={i} className="text-center text-[#455561] font-semibold">{p}</td>)}
                  <td className="text-center font-bold text-[#455561] bg-[#E9EDEB]">{outPar}</td>
                  {holePars.slice(9).map((p, i) => <td key={i + 9} className="text-center text-[#455561] font-semibold">{p}</td>)}
                  <td className="text-center font-bold text-[#455561] bg-[#E9EDEB]">{inPar}</td>
                  <td className="text-center font-bold text-[#455561] bg-[#E9EDEB]">{outPar + inPar}</td>
                  <td className="bg-[#E9EDEB]"></td>
                </tr>
                <tr className="bg-[#F4F6F5]">
                  <td className="px-2 py-1 text-[10px] text-[#B8C7C0] sticky left-0 bg-[#F4F6F5] z-10">SI</td>
                  {strokeIndexes.slice(0, 9).map((s, i) => <td key={i} className="text-center text-[10px] text-[#B8C7C0]">{s}</td>)}
                  <td className="bg-[#E9EDEB]"></td>
                  {strokeIndexes.slice(9).map((s, i) => <td key={i + 9} className="text-center text-[10px] text-[#B8C7C0]">{s}</td>)}
                  <td className="bg-[#E9EDEB]"></td><td className="bg-[#E9EDEB]"></td><td className="bg-[#E9EDEB]"></td>
                </tr>
              </thead>
              <tbody>
                {(frozen
                  ? teams.map((_, i) => i)
                  : standings.length > 0 ? standings.map(s => s.team_index) : teams.map((_, i) => i)
                )
                  .filter(teamIdx => {
                    // Freeze restriction overrides display filter
                    if (freezeTeamIdx != null) return teamIdx === freezeTeamIdx;
                    if (filterMode === 'all') return true;
                    if (filterMode === 'group' && filterTeamIdx !== null) return teamIdx === filterTeamIdx;
                    if (filterMode === 'player' && filterPlayerId) return (teams[teamIdx] || []).includes(filterPlayerId);
                    return true;
                  })
                  .map(teamIdx => {
                  const tk = String(teamIdx);
                  const teamPids = teams[teamIdx] || [];
                  const teamPlayers = teamPids.map(pid => players.find(p => p.id === pid)).filter(Boolean);
                  const tph = round && trip ? computeTeamPH(round, teamIdx, trip) : 0;
                  const teamName = groupDisplayName(round, teamIdx);
                  const teamStrokes = round?.team_hole_strokes?.[tk] || [];
                  const teamScores = round?.team_hole_scores?.[tk] || [];
                  const teamNrHoles = round?.team_hole_nr?.[tk] || [];
                  const teamDrivers = round?.team_drive_owner?.[tk] || [];
                  const driveCounts = driveCountsForTeam(round, teamIdx);

                  // Frozen holes for this team
                  const throughPos = frozen ? freezeAt : 18;
                  const frozenHoles = new Set();
                  if (frozen) {
                    for (let h = 0; h < 18; h++) {
                      if (!holeInPlayingRange(round, teamIdx, h, throughPos)) frozenHoles.add(h);
                    }
                  }

                  // NDB gross for NR holes
                  const ndbGross = (h) => holePars[h] + 2 + shotsOnHole(tph, strokeIndexes[h]);

                  // Driver position label: P{n} where n is 1-based index in team array
                  const driverLabel = (h) => {
                    const pid = teamDrivers[h];
                    if (!pid) return '';
                    const idx = teamPids.indexOf(pid);
                    return idx >= 0 ? `P${idx + 1}` : '';
                  };

                  // OUT/IN totals
                  let outVal = 0, inVal = 0, outPts = 0, inPts = 0;
                  for (let h = 0; h < 18; h++) {
                    if (frozenHoles.has(h)) continue;
                    const gross = teamStrokes[h] || 0;
                    const pts = teamScores[h] ?? -1;
                    const isNr = teamNrHoles.includes(h);
                    if (h < 9) {
                      outVal += isNr ? ndbGross(h) : gross;
                      outPts += isNr ? 0 : Math.max(0, pts);
                    } else {
                      inVal += isNr ? ndbGross(h) : gross;
                      inPts += isNr ? 0 : Math.max(0, pts);
                    }
                  }

                  // Final column
                  let finalCol;
                  if (roundFormat(round) === 'nsp') {
                    // Net: total gross - shots received over 18 holes (excluding frozen)
                    let netTotal = 0;
                    for (let h = 0; h < 18; h++) {
                      if (frozenHoles.has(h)) continue;
                      const s = teamStrokes[h] || 0;
                      if (s > 0) netTotal += s - shotsOnHole(tph, strokeIndexes[h]);
                    }
                    finalCol = netTotal;
                  } else {
                    // Stableford total
                    finalCol = outPts + inPts;
                  }

                  // Team finished? group_finished flag OR all 18 holes scored/NR
                  const isTeamFinished = (round?.group_finished || [])[teamIdx] === true ||
                    (() => { for (let h = 0; h < 18; h++) { if ((teamStrokes[h] || 0) <= 0 && !teamNrHoles.includes(h)) return false; } return true; })();

                  // Has the team scored at least one hole?
                  const hasScored = teamStrokes.some(s => s > 0);

                  // Per-player line data
                  const playerLines = teamPlayers.map(p => {
                    const ph = getEffectivePH(p, effectiveCourseForPlayer(round, p.id));
                    const dc = driveCounts[p.id] || 0;
                    const under = minDrives > 0 && dc < minDrives && isTeamFinished;
                    return { name: p.name, ph: `PH: ${formatPlayingHandicap(ph)}`, drives: dc, under };
                  });

                  const renderCell = (holeIdx) => {
                    if (frozenHoles.has(holeIdx)) {
                      return (
                        <td key={holeIdx} className="text-center py-1">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-xs text-[#B8C7C0] bg-[#F4F6F5]" title="Hidden (frozen)">
                            &#x1F512;
                          </span>
                        </td>
                      );
                    }
                    const isNr = teamNrHoles.includes(holeIdx);
                    if (isNr) {
                      const showNdb = !frozen && isStableford && displayMode === 'strokes';
                      return (
                        <td key={holeIdx} className="text-center py-1">
                          <div className="flex flex-col items-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[9px] font-bold text-[#E59000] bg-[#FFF3E0]" title="No Return">{showNdb ? ndbGross(holeIdx) : 'NR'}</span>
                            <span className="text-[8px] text-[#B8C7C0] leading-none mt-0.5">{driverLabel(holeIdx)}</span>
                          </div>
                        </td>
                      );
                    }
                    const gross = teamStrokes[holeIdx] || 0;
                    const pts = teamScores[holeIdx] ?? -1;
                    const showPoints = isStableford && displayMode === 'points';
                    const val = showPoints ? (pts >= 0 ? pts : null) : gross;
                    const empty = showPoints ? (pts < 0) : (gross <= 0);
                    return (
                      <td key={holeIdx} className="text-center py-1">
                        <div className="flex flex-col items-center">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold ${scoreColorClass(gross, holePars[holeIdx])}`}>
                            {empty ? '-' : val}
                          </span>
                          <span className="text-[8px] text-[#B8C7C0] leading-none mt-0.5">{driverLabel(holeIdx)}</span>
                        </div>
                      </td>
                    );
                  };

                  return (
                    <tr key={teamIdx} className="border-t border-[#E1E6E4]" data-testid={`sc-team-row-${teamIdx}`}>
                      <td className="px-2 py-2 sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="font-semibold text-[#1D242B] text-xs">{teamName}</span>
                          <span className="text-[9px] text-[#1B4332] font-bold bg-[#E8E5DB] rounded px-1 py-0.5 whitespace-nowrap">PH {formatPlayingHandicap(tph)}</span>
                        </div>
                        {playerLines.map((pl, i) => (
                          <div key={i} className={`text-[9px] leading-tight ${pl.under ? 'text-[#D32F2F] font-bold' : 'text-[#455561]'}`}>
                            {pl.name} ({pl.ph}){minDrives > 0 ? ` · ${pl.drives}/${minDrives}` : ''}
                          </div>
                        ))}
                      </td>
                      {Array.from({length: 9}, (_, i) => renderCell(i))}
                      <td className="text-center font-bold bg-[#F4F6F5] py-1">
                        {!hasScored ? <span className="text-[#B8C7C0]">–</span>
                          : isStableford && displayMode === 'points'
                          ? <span className="text-[#1B4332]">{outPts}</span>
                          : <span className="text-[#1D242B]">{outVal || '–'}</span>}
                      </td>
                      {Array.from({length: 9}, (_, i) => renderCell(i + 9))}
                      <td className="text-center font-bold bg-[#F4F6F5] py-1">
                        {!hasScored ? <span className="text-[#B8C7C0]">–</span>
                          : isStableford && displayMode === 'points'
                          ? <span className="text-[#1B4332]">{inPts}</span>
                          : <span className="text-[#1D242B]">{inVal || '–'}</span>}
                      </td>
                      <td className="text-center font-bold bg-[#F4F6F5] py-1">
                        {!hasScored ? <span className="text-[#B8C7C0]">–</span>
                          : isStableford && displayMode === 'points'
                          ? <span className="text-[#1B4332]">{outPts + inPts}</span>
                          : <span className="text-[#1D242B]">{(outVal + inVal) || '–'}</span>}
                      </td>
                      <td className="text-center font-bold text-[#1B4332] bg-[#E8E5DB]">
                        {!hasScored ? <span className="text-[#B8C7C0]">–</span>
                          : isStableford && displayMode === 'strokes' ? ((outVal + inVal) - tph) || '–' : finalCol}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
          )}

          {/* Branding footer */}
          <div className="bg-[#F4F6F5] text-center px-4 py-2 border-t border-[#E1E6E4]">
            <p className="text-[10px] text-[#455561] font-semibold">Fore Tour™ — Golf Scoring & Management · foretour.co.uk</p>
          </div>
        </div>
      </div>
    </div>
  );
}
